import { getBestRefreshToken, persistAccessToken, persistRefreshToken } from './authHeaders';
import { buildApiUrl } from '../lib/apiBase';
import { nextRequestGroupId, recordAuthCounter } from '../lib/authDiagnostics';


let refreshInFlight: Promise<string | null> | null = null;

export type RefreshAttemptInfo = {
  success: boolean;
  terminal: boolean;
  reason:
    | 'missing_refresh_token'
    | 'non_200'
    | 'missing_access_token'
    | 'exception'
    | 'success';
  status?: number;
  at: string;
};

let lastRefreshAttempt: RefreshAttemptInfo | null = null;

export function getLastRefreshAttemptInfo(): RefreshAttemptInfo | null {
  return lastRefreshAttempt;
}

/**
 * Exchange refresh_token for a new access_token (and optional rotated refresh).
 * Single-flight: concurrent callers share one in-flight refresh.
 * Uses same-origin /api/v1/auth/refresh (Vite proxy in dev).
 */
export async function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async (): Promise<string | null> => {
    try {
      const refreshToken = getBestRefreshToken();
      if (!refreshToken) {
        lastRefreshAttempt = {
          success: false,
          terminal: true,
          reason: 'missing_refresh_token',
          at: new Date().toISOString(),
        };
        recordAuthCounter('auth.refresh.failed', 1, { reason: 'missing_refresh_token' });
        return null;
      }

      const requestGroupId = nextRequestGroupId();
      const refreshResponse = await fetch(buildApiUrl('/api/v1/auth/refresh'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Request-Group-ID': requestGroupId,
          'X-Request-ID': `${requestGroupId}-refresh`,
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!refreshResponse.ok) {
        lastRefreshAttempt = {
          success: false,
          terminal: refreshResponse.status === 401 || refreshResponse.status === 403,
          reason: 'non_200',
          status: refreshResponse.status,
          at: new Date().toISOString(),
        };
        recordAuthCounter('auth.refresh.failed', 1, { reason: 'non_200', status: refreshResponse.status });
        return null;
      }

      const payload = await refreshResponse.json();
      const newAccessToken =
        payload && typeof payload.access_token === 'string' ? payload.access_token : null;
      if (!newAccessToken) {
        lastRefreshAttempt = {
          success: false,
          terminal: false,
          reason: 'missing_access_token',
          at: new Date().toISOString(),
        };
        recordAuthCounter('auth.refresh.failed', 1, { reason: 'missing_access_token' });
        return null;
      }

      persistAccessToken(newAccessToken);

      const newRefreshToken =
        payload && typeof payload.refresh_token === 'string' ? payload.refresh_token : null;
      if (newRefreshToken) {
        persistRefreshToken(newRefreshToken);
      }

      lastRefreshAttempt = {
        success: true,
        terminal: false,
        reason: 'success',
        at: new Date().toISOString(),
      };
      recordAuthCounter('auth.refresh.success');
      return newAccessToken;
    } catch {
      lastRefreshAttempt = {
        success: false,
        terminal: false,
        reason: 'exception',
        at: new Date().toISOString(),
      };
      recordAuthCounter('auth.refresh.failed', 1, { reason: 'exception' });
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}
